// Strava OAuth認証コールバック処理コンポーネント

import React, { useEffect, useState } from 'react';
import { Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { AuthManager } from '../utils/auth';

interface AuthCallbackProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const AuthCallback: React.FC<AuthCallbackProps> = ({ onSuccess, onError }) => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('認証処理中...');

  useEffect(() => {
    handleAuthCallback();
  }, []);

  const handleAuthCallback = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');

      // エラーパラメータのチェック
      if (error) {
        const errorDescription = urlParams.get('error_description') || '認証がキャンセルされました';
        throw new Error(errorDescription);
      }

      // 必要なパラメータのチェック
      if (!code || !state) {
        throw new Error('認証パラメータが不正です。再度ログインしてください。');
      }

      setMessage('認証トークンを取得中...');
      
      const authManager = AuthManager.getInstance();
      await authManager.exchangeCodeForToken(code, state);

      setStatus('success');
      setMessage('認証が完了しました。ダッシュボードに戻ります...');

      // 少し待ってからダッシュボードに戻る
      setTimeout(() => {
        onSuccess?.();
        // URLをクリーンアップしてホームに戻る
        window.history.replaceState({}, document.title, '/');
        window.location.href = '/';
      }, 2000);

    } catch (error) {
      console.error('Auth callback failed:', error);
      const errorMessage = error instanceof Error ? error.message : '認証に失敗しました';
      
      setStatus('error');
      setMessage(errorMessage);
      onError?.(errorMessage);

      // エラー時も少し待ってからホームに戻る
      setTimeout(() => {
        window.history.replaceState({}, document.title, '/');
        window.location.href = '/';
      }, 5000);
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader className="w-8 h-8 animate-spin text-blue-600" />;
      case 'success':
        return <CheckCircle className="w-8 h-8 text-green-600" />;
      case 'error':
        return <AlertCircle className="w-8 h-8 text-red-600" />;
    }
  };

  const getBackgroundColor = () => {
    switch (status) {
      case 'loading':
        return 'bg-blue-50';
      case 'success':
        return 'bg-green-50';
      case 'error':
        return 'bg-red-50';
    }
  };

  const getTextColor = () => {
    switch (status) {
      case 'loading':
        return 'text-blue-800';
      case 'success':
        return 'text-green-800';
      case 'error':
        return 'text-red-800';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className={`max-w-md w-full mx-4 p-8 rounded-lg shadow-lg ${getBackgroundColor()}`}>
        <div className="text-center">
          <div className="flex justify-center mb-4">
            {getIcon()}
          </div>
          <h2 className={`text-xl font-semibold mb-2 ${getTextColor()}`}>
            {status === 'loading' && 'Strava 認証処理'}
            {status === 'success' && '認証成功'}
            {status === 'error' && '認証エラー'}
          </h2>
          <p className={`text-base ${getTextColor()}`}>
            {message}
          </p>
          {status === 'error' && (
            <button
              onClick={() => window.location.href = '/'}
              className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
            >
              ダッシュボードに戻る
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;